/**
 * SandboxOverview — Dashboard card showing real sandbox container state.
 *
 * Shows SandboxStatus from useSandboxStore with lifecycle badge,
 * container info, and quick-launch / navigate actions.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Server, ExternalLink, Play, RefreshCw, Clock } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { useSandboxStore } from "@/stores/useSandboxStore";
import type { SandboxLifecycleState } from "@ajnt/shared";

/* ------------------------------------------------------------------ */
/*  State → badge mapping                                              */
/* ------------------------------------------------------------------ */

const stateBadge: Record<
  SandboxLifecycleState,
  { label: string; variant: "success" | "warning" | "danger" | "default" | "primary" }
> = {
  creating: { label: "Creating", variant: "warning" },
  provisioning: { label: "Provisioning", variant: "warning" },
  active: { label: "Ready", variant: "success" },
  sleeping: { label: "Sleeping", variant: "default" },
  waking: { label: "Waking", variant: "warning" },
  error: { label: "Error", variant: "danger" },
  terminated: { label: "Terminated", variant: "default" },
};

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SandboxOverview() {
  const { sandbox, loading, fetchSandboxes, launchSandbox, wakeSandbox } =
    useSandboxStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSandboxes();
  }, [fetchSandboxes]);

  const state = sandbox?.state ?? null;
  const badge = state ? stateBadge[state] : null;
  const canQuickLaunch = !sandbox || state === "terminated";
  const canWake = state === "sleeping";
  const isTransitional = state === "creating" || state === "provisioning" || state === "waking";

  return (
    <Card data-testid="sandbox-overview">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-5 w-5 text-primary" />
          Sandbox
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && !sandbox ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : !sandbox ? (
          <EmptyState
            title="No sandbox"
            description="Launch a sandbox to start coding with AI"
            className="py-6"
          />
        ) : (
          <div className="space-y-4">
            {/* Status row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Pulsing dot */}
                <span className="relative flex h-2.5 w-2.5">
                  {(state === "active" || isTransitional) && (
                    <span
                      className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
                        state === "active" ? "bg-emerald-400" : "bg-amber-400"
                      }`}
                    />
                  )}
                  <span
                    className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                      state === "active"
                        ? "bg-emerald-500"
                        : state === "error"
                          ? "bg-red-500"
                          : state === "sleeping" || state === "terminated"
                            ? "bg-gray-500"
                            : "bg-amber-500"
                    }`}
                  />
                </span>
                {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
                {isTransitional && <Spinner />}
              </div>

              {sandbox.instanceType && (
                <span className="text-xs text-muted-foreground">
                  {sandbox.instanceType}
                </span>
              )}
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {sandbox.uptimeSeconds != null && sandbox.uptimeSeconds > 0 && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{formatUptime(sandbox.uptimeSeconds)} uptime</span>
                </div>
              )}
              {sandbox.lastActiveAt && (
                <div className="text-muted-foreground text-xs">
                  Last active {formatDate(sandbox.lastActiveAt)}
                </div>
              )}
              {sandbox.containerId && (
                <div className="col-span-2 font-mono text-xs text-muted-foreground truncate">
                  ID: {sandbox.containerId}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          {canQuickLaunch && (
            <Button
              size="sm"
              onClick={() => launchSandbox()}
              loading={loading}
            >
              <Play className="h-3.5 w-3.5" />
              Quick Launch
            </Button>
          )}

          {canWake && (
            <Button
              size="sm"
              onClick={() => wakeSandbox(sandbox!.id)}
              loading={loading}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Wake
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard/sandbox")}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Manage
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
